import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SERVICE_TYPES } from '../../../graphql/queries';
import { logger } from '../../../utils/logger';

export default function ServiceTypeFilter({ selectedServiceTypes, onChange, userRole }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { loading, error, data } = useQuery(GET_SERVICE_TYPES);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) return null;
  if (error) {
    logger.error('Error loading service types', error);
    return null;
  }

  // Filter service types based on user role
  const availableServiceTypes = data?.serviceTypes?.filter(type =>
    type.isActive && (['ADMIN', 'GUEST_APP_MANAGER'].includes(userRole) || type.allowedRoles?.includes(userRole))
  ) || [];

  // Define explicit service type ordering with exact names
  // The lower the rank number, the higher it appears in the list
  const getServiceTypeRank = (title) => {
    const lowerTitle = title.toLowerCase();

    // First tier - Top priority built-in services
    if (lowerTitle === "clean" || lowerTitle === "cleaning") return 1;
    if (lowerTitle === "pre-arrival inspection" || lowerTitle === "prearrival inspection") return 2;
    if (lowerTitle === "walkthrough" || lowerTitle === "walk-through") return 3;

    // Second tier - Other common built-in services
    if (lowerTitle === "departure clean") return 10;
    if (lowerTitle === "arrival clean") return 11;
    if (lowerTitle === "standard clean") return 12;
    if (lowerTitle === "deep clean") return 13;

    // Third tier - Services containing priority keywords but not exact matches
    if (lowerTitle.includes("clean") && !lowerTitle.includes("detail")) return 20;
    if (lowerTitle.includes("inspection")) return 21;
    if (lowerTitle.includes("walk")) return 22;

    // Fourth tier - Detailed clean explicitly ranked lower than primary services
    if (lowerTitle.includes("detailed clean") || lowerTitle.includes("detail clean")) return 30;

    // Everything else
    return 100;
  };

  // Sort service types according to the defined rank
  const sortedServiceTypes = [...availableServiceTypes].sort((a, b) => {
    const rankA = getServiceTypeRank(a.title);
    const rankB = getServiceTypeRank(b.title);

    // First compare by rank
    if (rankA !== rankB) {
      return rankA - rankB;
    }

    // If same rank, sort alphabetically
    return a.title.localeCompare(b.title);
  });

  logger.debug('Sorted service types', sortedServiceTypes.map(t => ({
    title: t.title,
    rank: getServiceTypeRank(t.title)
  })));

  const handleServiceTypeToggle = (typeId) => {
    if (selectedServiceTypes.includes(typeId)) {
      onChange(selectedServiceTypes.filter(id => id !== typeId));
    } else {
      onChange([...selectedServiceTypes, typeId]);
    }
  };

  const selectedTypes = availableServiceTypes.filter(type =>
    selectedServiceTypes.includes(type.id)
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor="serviceTypeFilter" className="block text-sm font-medium text-gray-700 mb-1">
        Service Types
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <span className="flex items-center">
          {selectedTypes.length > 0 ? (
            <span className="flex items-center space-x-1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: `${selectedTypes[0].color}20`, color: selectedTypes[0].color }}>
                {selectedTypes[0].title}
              </span>
              {selectedTypes.length > 1 && (
                <span className="text-gray-500">+{selectedTypes.length - 1}</span>
              )}
            </span>
          ) : (
            'Select service types'
          )}
        </span>
        <svg
          className={`h-5 w-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="fixed left-0 right-0 mt-1 mx-auto w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
          style={{
            zIndex: 9999,
            position: 'absolute',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {sortedServiceTypes.map(type => (
            <div
              key={type.id}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${
                selectedServiceTypes.includes(type.id) ? 'bg-blue-50' : ''
              }`}
              onClick={() => handleServiceTypeToggle(type.id)}
            >
              <div className="flex items-center">
                <span
                  className="inline-block w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: type.color }}
                />
                <span className="block truncate">{type.title}</span>
                {selectedServiceTypes.includes(type.id) && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedTypes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedTypes.map(type => (
            <span
              key={type.id}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: `${type.color}20`, color: type.color }}
            >
              {type.title}
              <button
                type="button"
                onClick={() => handleServiceTypeToggle(type.id)}
                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-20"
              >
                <span className="sr-only">Remove {type.title}</span>
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
